import { useEffect, useRef } from 'react';

/**
 * @param enabled When false, no document listener is registered (avoids dozens of
 * rows each attaching `click` on `document`, which can reorder/race with React handlers).
 */
function useClickOutside(handler: (event: MouseEvent) => void, enabled = true) {
  const ref = useRef(null);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        ref.current &&
        !(ref.current as HTMLElement).contains(event.target as Node)
      ) {
        handler(event);
      }
    };

    // Use `click` (bubble phase, default) so it runs after inner `onClick`
    // handlers on rows, dots, and menu items. `mousedown` fires first and
    // races with those clicks (close then reopen, wrong open row, etc.).
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handler, enabled]);

  return ref;
}

export { useClickOutside };
